import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { useDraft2 } from '../../../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../../../Legacy';
import { ApiClient } from '../../../../api/ApiClient';
import { v2 } from '../../../../api/ApiInterfaceV2';
import { LlmProviderData } from '../../../../api/endpoints/llm';
import { Button, Input, Select, usePrompt } from '../../../../ui';
import { ResultInfoForMutation } from '../../../../ui/ResultInfo';
import { useResolve } from '../../../Container';
import { Copy, Flexer, Spacer } from '../../../dashboard/elements/LayoutElements';
import { Popup, PopupProps } from '../../../popups/Popup';

export const addProviderPopup = () => ({ ui: AddProviderPopup });

export interface AddModelFormData {
    provider: LlmProviderData;
    apiKey: string;
    organizationId?: string;
    endpoint?: string;
}

const useProviderQuery = () => {
    const api = useResolve(ApiClient);
    return useQuery({
        queryKey: ['llm.providers.list'],
        queryFn: async () => {
            return await api.exec(v2.llm.providers.list());
        }
    });
};

const useAddMutation = () => {

    const api = useResolve(ApiClient);
    const qc = useQueryClient();

    return useMutation({
        mutationFn: async (data: AddModelFormData) => {
            return await api.exec(v2.llm.instances.add({
                providerId: data.provider.id,
                apiKey: data.apiKey,
                organizationId: data.organizationId,
                endpoint: data.endpoint,
            }));
        },
        onSuccess: () => {
            // Invalidate the llm.list query to refresh the data
            qc.invalidateQueries(['llm.instances.list']);
        }
    });
};

export const AddProviderPopup: LFC<PopupProps> = ({ action, dismiss }) => {

    const providerQuery = useProviderQuery();
    const addMutation = useAddMutation();

    const { draft, patchDraft } = useDraft2<AddModelFormData>({
        provider: null,
        apiKey: '',
    });

    useEffect(() => {
        if (providerQuery.data) {
            patchDraft({ provider: providerQuery.data[0] });
        }
    }, [providerQuery.data]);

    const invalid = !draft.provider || !draft.apiKey.trim();

    const onSubmit = () => {
        if (invalid) return;
        addMutation.mutateAsync(draft).then(() => action(true));
    };

    const promptRef = usePrompt({
        onAction(confirm: boolean) {
            if (addMutation.isLoading) return;
            if (!confirm) dismiss();
            else if (!invalid) onSubmit();
        },
    });

    return <Popup w={40} ref={promptRef}>
        <Popup.Close onAction={dismiss} />
        <Popup.Title>Add Models</Popup.Title>
        <Popup.Content>
            <Copy>
                Please select and configure the provider you want to add models for:
            </Copy>

            <Flexer flex="column" gap="1" padding="1 0">
                <Input.Frame>
                    <Input.Label>Provider:</Input.Label>
                    {providerQuery.isLoading ? (
                        <Input value="Loading providers..." />
                    ) : providerQuery.isError ? (
                        <div>Error loading providers.</div>
                    ) : (
                        <Select
                            items={providerQuery.data}
                            selected={draft.provider}
                            onChange={x => patchDraft({ provider: x })}
                        />
                    )}
                </Input.Frame>

                <Input
                    label="API Key"
                    placeholder="Enter API key"
                    value={draft.apiKey}
                    autoComplete="off"
                    onChange={x => patchDraft({ apiKey: x })}
                />

                {'endpointFormat' in (draft.provider?.params ?? {}) && <>
                    <Input
                        label="Endpoint URL"
                        placeholder={draft.provider.params.endpointFormat}
                        value={draft.endpoint || ''}
                        onChange={x => patchDraft({ endpoint: x })}
                    />
                </>}

                {'organizationId' in (draft.provider?.params ?? {}) && <>
                    <Input
                        label="Organization ID"
                        placeholder={draft.provider.params.organizationId}
                        value={draft.organizationId || ''}
                        onChange={x => patchDraft({ organizationId: x })}
                    />
                </>}

            </Flexer>

            <Spacer f={1} />
            <ResultInfoForMutation mutation={addMutation} />
        </Popup.Content>
        <Popup.Actions>
            <Button disabled={invalid || addMutation.isLoading} variant="primary" onAction={onSubmit}>
                {addMutation.isLoading ? 'Adding...' : 'Add Provider'}
            </Button>
        </Popup.Actions>
    </Popup>;
};
